import { create } from "zustand"

import {
  GetProductCostBreakdownListDocument,
  type GetProductCostBreakdownListQuery,
  type ProductWarehouseItem,
  type ProductWarehouseListSortField,
  type SortingOrder,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type ProductCostBreakdownSearchFilters = SearchFilters & {
  productId: number
  field?: ProductWarehouseListSortField
  order?: SortingOrder
}

const getProductCostBreakdownId = ({ goodsReceipt, ...rest }: ProductWarehouseItem): string =>
  JSON.stringify({ ...rest, goodsReceiptId: goodsReceipt?.goodsReceiptId })

const deduplicateProductCostBreakdowns = (
  newData: ProductWarehouseItem[],
  existingData: ProductWarehouseItem[]
): ProductWarehouseItem[] => {
  const seen = new Set(existingData.map((item) => getProductCostBreakdownId(item)))
  return [...existingData, ...newData.filter((item) => !seen.has(getProductCostBreakdownId(item)))]
}

export const useProductCostBreakdownsSearchStore = create(
  createSearchStore<ProductWarehouseItem, ProductCostBreakdownSearchFilters, GetProductCostBreakdownListQuery>({
    documentNode: GetProductCostBreakdownListDocument,
    deduplicateData: deduplicateProductCostBreakdowns,
    getQueryInput: (filters: ProductCostBreakdownSearchFilters, offset: number, limit: number) => {
      const { field, order, productId } = filters
      return {
        filter: { productId },
        offset,
        limit,
        sort: field && order ? { field, order } : undefined,
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.product.listByProductWarehouse.__typename,
      data:
        data.product.listByProductWarehouse.__typename === "ProductWarehouseListSuccess"
          ? (data.product.listByProductWarehouse.items as ProductWarehouseItem[])
          : [],
      error:
        data.product.listByProductWarehouse.__typename === "ProductWarehouseListFailure"
          ? data.product.listByProductWarehouse.error
          : undefined,
    }),
  })
)
